import { useState, useEffect, useMemo } from 'preact/hooks'
import { X, ChevronDown, ChevronRight, Check, AlertTriangle, Download } from 'lucide-preact'
import { TextBoxField, Select, DateTimePicker, FileUpload, TimeZoneCombobox } from '@/components'
import type { SelectOption } from '@/components/common/Select'
import { WebsterIcon } from '@/components/aiChat/WebsterIcon'
import { WebsterBotStatusBadge } from './WebsterBotStatusBadge'
import { humanizeSubCode } from './websterStatus'
import { useLookups } from '@/hooks/useLookups'
import { useForm, Controller } from 'react-hook-form'
import { aiChatSuppressed } from '@/state/aiChat'
import { sanitizeDateTimeInput } from '@/utils/dateTimeFormatters'
import type { TouchPointViewDto } from '@/types/touchPoint'
import type { TouchPointFormData } from './modals/TouchPointModal'

interface Props {
  open: boolean
  touchPoint?: TouchPointViewDto | null
  onSave: (data: TouchPointFormData, file?: File) => Promise<void>
  onClose: () => void
  saving: boolean
  isCampaignLevel?: boolean
}

// Meeting-editor form shape (a Meeting-typed subset of TouchPointFormData).
type MeetingForm = {
  Name: string
  MeetingType: string
  TimeZone: string
  StartTime: string
  EndTime: string
  MeetingUrlLink: string
}

type Platform = 'meet' | 'teams' | 'zoom' | 'other' | null

// Detect the conferencing platform from the pasted link. Meet + Teams are the platforms Webster
// can currently join; Zoom is recognised but not yet supported (needs one-time recall setup).
function detectPlatform(url: string | undefined): Platform {
  if (!url || !url.trim()) return null
  const u = url.toLowerCase()
  if (u.includes('meet.google.com')) return 'meet'
  if (u.includes('teams.microsoft.com') || u.includes('teams.live.com')) return 'teams'
  if (u.includes('zoom.us')) return 'zoom'
  return 'other'
}

const PLATFORM_LABEL: Record<'meet' | 'teams' | 'zoom', string> = {
  meet: 'Google Meet',
  teams: 'Microsoft Teams',
  zoom: 'Zoom',
}

/**
 * Meeting touchpoint editor as a right-side panel (replaces the shared TouchPointModal for meetings).
 * The meeting link is the hero: pasting a supported link (Google Meet or Teams) arms Webster Notetaker
 * to join at the start time and record + transcribe. Zoom is recognised but flagged "coming soon".
 * The manual transcript upload is kept as a demoted fallback for meetings Webster can't cover.
 */
export function MeetingEditorPanel({
  open, touchPoint, onSave, onClose, saving, isCampaignLevel,
}: Props) {
  const isEditMode = !!touchPoint
  const { getCampaignMeetingTypes, getWebMeetingTypes } = useLookups()
  const meetingTypes = isCampaignLevel ? getCampaignMeetingTypes() : getWebMeetingTypes()

  // Non-form UI state.
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState<string>('')
  const [showManualUpload, setShowManualUpload] = useState(false)
  // Per-meeting opt-out. Defaults on; persisted via the touchpoint DTO once the API field lands (Step 2).
  const [sendWebster, setSendWebster] = useState(true)

  const buildDefaults = (): MeetingForm => ({
    Name: touchPoint?.Name || '',
    MeetingType: touchPoint?.MeetingProperties?.Type || '',
    TimeZone: touchPoint?.MeetingProperties?.TimeZone || '',
    StartTime: sanitizeDateTimeInput(touchPoint?.MeetingProperties?.StartTime),
    EndTime: sanitizeDateTimeInput(touchPoint?.MeetingProperties?.EndTime),
    MeetingUrlLink: touchPoint?.MeetingProperties?.MeetingUrlLink || '',
  })

  // Plain react-hook-form: a meeting has nothing worth generating with Webster (just a name), and the
  // chat is hidden while this panel is open anyway, so the AI-form wiring would be unusable here.
  const form = useForm<MeetingForm>({
    mode: 'onChange',
    defaultValues: buildDefaults(),
  })
  const { register, control, watch, reset, trigger, getValues, formState: { errors } } = form

  const linkValue = watch('MeetingUrlLink')
  const startValue = watch('StartTime')
  const platform = detectPlatform(linkValue)
  const supported = platform === 'meet' || platform === 'teams'

  // Server-managed Webster state, populated by the recall.ai webhooks (read-only on the view DTO).
  const meeting = touchPoint?.MeetingProperties
  const botStatus = meeting?.BotStatus
  const botSubCode = meeting?.BotStatusSubCode
  // "Not Scheduled" is the empty state (no bot); the toggle + platform hint already convey it.
  const showBotStatus = !!botStatus && botStatus !== 'Not Scheduled'

  // Reseed when opened or the edited meeting changes.
  useEffect(() => {
    if (!open) return
    reset(buildDefaults())
    setSelectedFile(null)
    setExistingAttachmentUrl(touchPoint?.MeetingProperties?.Attachment || '')
    setShowManualUpload(false)
    // Seed from the persisted opt-out on edit; default on for new meetings.
    setSendWebster(touchPoint?.MeetingProperties?.SendWebster ?? true)
  }, [open, touchPoint?.Id])

  // Keep End validation reactive to Start — including when Start is cleared, so a stale
  // "must be after start time" error doesn't linger.
  useEffect(() => { if (open) trigger('EndTime') }, [startValue, open, trigger])

  // Hide the floating Webster chat while the panel is open (matches the other side panels).
  useEffect(() => {
    aiChatSuppressed.value = open
    return () => { aiChatSuppressed.value = false }
  }, [open])

  // Escape closes, never mid-save.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, saving, onClose])

  const meetingTypeOptions: SelectOption[] = useMemo(
    () => meetingTypes.map(t => ({ value: t.Description, label: t.Description })), [meetingTypes])

  const handleSave = async () => {
    const valid = await trigger()
    if (!valid) return
    const v = getValues()
    // Reuse the shared TouchPointFormData contract so MeetingsTab.handleSave is unchanged.
    const data: TouchPointFormData = {
      Name: v.Name,
      Type: 'Meeting',
      ScheduledDate: '',
      MeetingType: v.MeetingType,
      TimeZone: v.TimeZone,
      StartTime: v.StartTime,
      EndTime: v.EndTime,
      MeetingUrlLink: v.MeetingUrlLink,
      // Only arm Webster for a platform it can actually join; mirrors the toggle's displayed state.
      SendWebster: sendWebster && supported,
      ETag: touchPoint?.ETag,
    }
    await onSave(data, selectedFile || undefined)
  }

  if (!open) return null

  return (
    <div class="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
      <div class="flex-1 bg-slate-900/40" onClick={() => !saving && onClose()} />
      <div class="w-full sm:w-[480px] lg:w-[540px] ml-auto h-full border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl flex flex-col">
        {/* Header */}
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
            {isEditMode ? 'Edit meeting' : 'New meeting'}
          </h3>
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            class="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close panel"
          >
            <X class="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <TextBoxField
            control={control}
            name="Name"
            density="compact"
            label="Name"
            required
            maxLength={200}
            placeholder="e.g., Discovery call — Acme"
            rules={{ required: 'Name is required', maxLength: { value: 200, message: 'Name must be 200 characters or less' } }}
          />

          {/* Meeting link — the hero field */}
          <div>
            <TextBoxField
              control={control}
              name="MeetingUrlLink"
              density="compact"
              label="Meeting link"
              type="url"
              placeholder="Paste a Google Meet or Teams link"
            />
            {platform && (
              <div class="mt-2">
                {supported && (
                  <span class="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-green-700 dark:text-green-400">
                    <span class="inline-flex w-4 h-4 items-center justify-center rounded-full bg-green-500 text-white">
                      <Check class="w-2.5 h-2.5" />
                    </span>
                    {PLATFORM_LABEL[platform as 'meet' | 'teams']} detected
                  </span>
                )}
                {platform === 'zoom' && (
                  <span class="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-amber-700 dark:text-amber-500">
                    <AlertTriangle class="w-3.5 h-3.5" />
                    Zoom support is coming soon. Webster can’t record this one yet.
                  </span>
                )}
                {platform === 'other' && (
                  <span class="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-amber-700 dark:text-amber-500">
                    <AlertTriangle class="w-3.5 h-3.5" />
                    Unsupported link. Webster records Google Meet and Teams.
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Webster row — quiet by default, expressive when armed */}
          <div class="flex items-start gap-3 py-0.5">
            <WebsterIcon size={26} className="shrink-0 mt-0.5" />
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between gap-3">
                <span class="font-semibold text-[13.5px] text-slate-900 dark:text-slate-100">Send Webster Notetaker</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={sendWebster && supported}
                  disabled={!supported}
                  onClick={() => supported && setSendWebster(v => !v)}
                  class={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${sendWebster && supported ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'} ${!supported ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span class={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${sendWebster && supported ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <p class="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                {supported
                  ? 'Joins at the start time, records, and transcribes into this meeting.'
                  : platform === 'zoom'
                    ? 'Available for Zoom soon. Add a Meet or Teams link, or upload a transcript below.'
                    : 'Add a Google Meet or Teams link to record this meeting automatically.'}
              </p>
              {touchPoint && showBotStatus && (
                <div class="mt-2.5 flex flex-col gap-1">
                  <WebsterBotStatusBadge status={botStatus} subCode={botSubCode} />
                  {botStatus === 'Failed' && botSubCode && (
                    <span class="text-[11.5px] text-red-600/90 dark:text-red-400/90 leading-snug">
                      {humanizeSubCode(botSubCode)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div class="h-px bg-slate-100 dark:bg-slate-700" />

          {/* Supporting fields */}
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              density="compact"
              label="Meeting type"
              required
              options={meetingTypeOptions}
              placeholder="Select type"
              error={errors.MeetingType?.message}
              {...register('MeetingType', { required: 'Meeting type is required' })}
            />
            <Controller
              name="TimeZone"
              control={control}
              rules={{ required: 'Time zone is required' }}
              render={({ field, fieldState }) => (
                <TimeZoneCombobox
                  density="compact"
                  label="Time zone"
                  required
                  name="TimeZone"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DateTimePicker
              label="Start"
              density="compact"
              required
              error={errors.StartTime?.message}
              {...register('StartTime', { required: 'Start time is required' })}
            />
            <DateTimePicker
              label="End"
              density="compact"
              required
              error={errors.EndTime?.message}
              {...register('EndTime', {
                required: 'End time is required',
                validate: (value: string) =>
                  !startValue || !value || new Date(value) > new Date(startValue) || 'End time must be after start time',
              })}
            />
          </div>

          {/* Manual transcript — demoted fallback */}
          <div class="pt-1">
            <button
              type="button"
              onClick={() => setShowManualUpload(v => !v)}
              class="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              {showManualUpload ? <ChevronDown class="w-3.5 h-3.5" /> : <ChevronRight class="w-3.5 h-3.5" />}
              Add a transcript manually
            </button>
            {(showManualUpload || (!!existingAttachmentUrl && !selectedFile)) && (
              <div class="mt-3 space-y-2">
                <p class="text-[12px] text-slate-500 dark:text-slate-400">
                  For meetings Webster can’t record (no link, already happened, or external).
                </p>
                {existingAttachmentUrl && !selectedFile && (
                  <a
                    href={existingAttachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 text-[12.5px] text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <Download class="w-4 h-4" /> Current transcript
                  </a>
                )}
                <FileUpload
                  selectedFile={selectedFile}
                  onFileChange={setSelectedFile}
                  accept=".json"
                  maxSizeMB={10}
                  helperText="JSON files (max 10MB)"
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div class="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            class="px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving && <div class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />}
            {saving ? 'Saving…' : 'Save meeting'}
          </button>
        </div>
      </div>
    </div>
  )
}
